import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { Sparkles, Eye, EyeOff, Crown } from "lucide-react";
import { adminApi } from "@/admin/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const Toggle = ({ on, onChange, label, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!on)}
    disabled={disabled}
    className={cn(
      "flex items-center gap-2 px-3 py-1.5 rounded-full border transition mono text-[10px] uppercase tracking-wider",
      on
        ? "bg-totem-green/15 border-totem-green/60 text-totem-green"
        : "bg-totem-surface border-totem-border text-totem-textDim",
      disabled && "opacity-50 cursor-not-allowed"
    )}
  >
    <span
      className={cn(
        "inline-block w-2 h-2 rounded-full",
        on ? "bg-totem-green" : "bg-totem-textFaint"
      )}
    />
    {label}
  </button>
);

export default function AdminFeatures() {
  const { admin } = useOutletContext();
  const isSuper = admin?.role === "super_admin";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-flags"],
    queryFn: async () => (await adminApi.get("/feature-flags")).data,
    staleTime: 30_000,
  });

  const patch = useMutation({
    mutationFn: async ({ key, body }) => (await adminApi.patch(`/feature-flags/${key}`, body)).data,
    onSuccess: () => {
      toast.success("Feature flag updated");
      qc.invalidateQueries({ queryKey: ["admin-flags"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const flags = data?.flags || [];

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Admin</div>
        <h1 className="display-heading text-3xl">Feature Flags</h1>
        {!isSuper && (
          <div className="mt-3 text-xs text-totem-amber border border-totem-amber/40 bg-totem-amber/10 rounded px-3 py-2">
            Toggling flags requires super admin.
          </div>
        )}
      </div>

      <div className="space-y-3">
        {isLoading && <div className="totem-card h-24 animate-pulse" />}
        {flags.map((f) => (
          <div key={f.feature_key} data-testid={`admin-flag-${f.feature_key}`} className="totem-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="display-heading text-base">{f.label}</div>
                  {f.is_premium && (
                    <span className="mono text-[9px] uppercase bg-totem-gold/15 text-totem-gold border border-totem-gold/40 rounded px-1.5 py-0.5 flex items-center gap-1">
                      <Crown size={9} /> Premium
                    </span>
                  )}
                </div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-0.5">
                  {f.feature_key}
                </div>
                <p className="text-xs text-totem-textDim mt-2 leading-relaxed">{f.description}</p>
                {f.updated_at && (
                  <div className="mono text-[9px] text-totem-textFaint mt-2 uppercase">
                    Updated {new Date(f.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Toggle
                  on={f.is_visible}
                  disabled={!isSuper || patch.isPending}
                  onChange={(v) => patch.mutate({ key: f.feature_key, body: { is_visible: v } })}
                  label={f.is_visible ? <><Eye size={11} className="inline mr-1" />Visible</> : <><EyeOff size={11} className="inline mr-1" />Hidden</>}
                />
                <Toggle
                  on={f.is_premium}
                  disabled={!isSuper || patch.isPending}
                  onChange={(v) => patch.mutate({ key: f.feature_key, body: { is_premium: v } })}
                  label={f.is_premium ? <><Sparkles size={11} className="inline mr-1" />Premium</> : "Free"}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
